import { useMemo } from 'react';

import { collection, query } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { useFirestoreQuery, useFirestoreRealtimeBatched } from '@src/packages/utils';
import useGetEventTypes from '@src/packages/utils/hooks/useGetEventTypes';

export const useDashboardData = ({ orgId, boardSelected, refreshChart }) => {
  const componentsCollectionRef = useMemo(() => {
    const collectionRef = {
      charts: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/charts`,
      tables: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/tables`,
      pieCharts: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/pie_charts`,
      countCharts: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/count_charts`,
      counterCharts: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/counter_charts`,
      barCharts: `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/bar_charts`,
    };
    if (boardSelected.divId) {
      collectionRef.charts = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/charts`;
      collectionRef.tables = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/tables`;
      collectionRef.pieCharts = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/pie_charts`;
      collectionRef.countCharts = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/count_charts`;
      collectionRef.counterCharts = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/counter_charts`;
      collectionRef.barCharts = `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.DIVISIONS}/${boardSelected.divId}/${FSCollections.DASHBOARDS}/${boardSelected.id}/bar_charts`;
    }
    return collectionRef;
  }, [orgId, boardSelected]);

  // CHARTS
  const { data: charts = [], loading: chartsLoading } = useFirestoreQuery(
    collection(db, componentsCollectionRef.charts),
    [orgId, boardSelected?.id],
  );

  // TABLES
  const { data: tables = [], loading: tablesLoading } = useFirestoreRealtimeBatched(
    [query(collection(db, componentsCollectionRef.tables))],
    [orgId, refreshChart, boardSelected?.id],
  );

  // PIE CHARTS
  const { data: pieCharts = [], loading: pieChartsLoading } = useFirestoreQuery(
    collection(db, componentsCollectionRef.pieCharts),
    [orgId, boardSelected?.id],
  );

  // COUNT CHARTS
  const { data: countCharts = [], loading: countChartsLoading } = useFirestoreQuery(
    collection(db, componentsCollectionRef.countCharts),
    [orgId, boardSelected?.id],
  );

  // EVENT TYPES
  const { data: eventTypes, loading: eventLoading } = useGetEventTypes();

  // COUNTERS
  const { data: counterCharts = [], loading: counterChartsLoading } = useFirestoreQuery(
    collection(db, componentsCollectionRef.counterCharts),
    [orgId, boardSelected?.id],
  );

  // BAR CHARTS
  const { data: barCharts = [], loading: barChartsLoading } = useFirestoreQuery(
    collection(db, componentsCollectionRef.barCharts),
    [orgId, boardSelected?.id],
  );
  
  const loading =
    chartsLoading || tablesLoading || pieChartsLoading || eventLoading || countChartsLoading || counterChartsLoading || barChartsLoading;

  return {
    charts,
    tables,
    pieCharts,
    countCharts,
    counterCharts,
    barCharts,
    eventTypes,
    loading,
    componentsCollectionRef,
  };
};
